import axios from 'axios';
import {
  Strategy,
  Instrument,
  Resolution,
  Backtest,
  BacktestRunRequest,
  BacktestRunResult,
  TradeDetail,
  TradeSignalRequest,
  TradeSignalResponse,
  Candle,
  ConfigCategory,
  UpdateConfigRequest
} from '../types'
import { FundSummary, FundTransaction, DepositRequest, WithdrawRequest } from '../types/fund.types'
import { AnalystReview, AgentSignal } from '../types/monitor.types';
import { config } from '../config/api';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach JWT token
api.interceptors.request.use((reqConfig) => {
  const token = localStorage.getItem('tradelab_token');
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// Response interceptor: redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('tradelab_token');
      localStorage.removeItem('tradelab_token_expiry');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Lookups API
export const lookupsApi = {
  getStrategies: async (): Promise<Strategy[]> => {
    const response = await api.get('/Lookups/strategies');
    return response.data.strategies;
  },

  getInstruments: async (): Promise<Instrument[]> => {
    const response = await api.get('/Lookups/instruments');
    return response.data.instruments;
  },

  getResolutions: async (): Promise<Resolution[]> => {
    const response = await api.get('/Lookups/resolutions');
    return response.data.resolutions;
  },

  getBacktests: async (): Promise<Backtest[]> => {
    const response = await api.get('/Lookups/backtests');
    return response.data.backtests;
  },
};

// Backtest API
export const backtestApi = {
  runBacktest: async (request: BacktestRunRequest): Promise<BacktestRunResult> => {
    const response = await api.post('/Backtest/run', request);
    // Backend returns {success: true, data: {...}}
    return response.data.data || response.data;
  },

  getBacktestTrades: async (backtestId: number): Promise<{ backtest_id: number; total_trades: number; trades: TradeDetail[] }> => {
    const response = await api.get(`/Backtest/trades/${backtestId}`);
    // Backend returns {success: true, data: {...}}
    return response.data.data || response.data;
  },

  analyzeBacktestResults: async (backtestId: number): Promise<any> => {
    const response = await api.get(`/Backtest/analyze-trades/${backtestId}`);
    return response.data.data || response.data;
  },

  getChartData: async (backtestId: number): Promise<any> => {
    const response = await api.get(`/Backtest/chart-data/${backtestId}`);
    return response.data.data || response.data;
  },

  getAIInsights: async (backtestId: number): Promise<any> => {
    const response = await api.get(`/Backtest/ai-insights/${backtestId}`);
    return response.data.data || response.data;
  },

  fetchHistoricalData: async (params: {
    userId: number;
    epic: string;
    resolution: string;
    fromDate: string;
    toDate: string;
  }): Promise<any> => {
    const response = await api.post('/Backtest/fetch-historical-data', params);
    return response.data;
  },

  deleteBacktest: async (backtestId: number): Promise<any> => {
    const response = await api.delete(`/Backtest/${backtestId}`);
    return response.data;
  },

  getRecentCandles: async (epic: string, resolution: string): Promise<any> => {
    const response = await api.get(`/Backtest/recent-candles?epic=${epic}&resolution=${resolution}`);
    return response.data;
  },

  getAllCandles: async (epic: string, resolution: string): Promise<Candle[]> => {
    const response = await api.get(`/Backtest/all-candles?epic=${epic}&resolution=${resolution}`);
    return response.data.data || response.data;
  },
};

// Manual Trading API
export const manualTradingApi = {
  sendSignal: async (request: TradeSignalRequest): Promise<TradeSignalResponse> => {
    const response = await api.post('/ManualTrading/signal', request);
    return response.data;
  },

  getTrades: async (epic?: string, resolution?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (epic) params.append('epic', epic);
    if (resolution) params.append('resolution', resolution);

    const response = await api.get(`/ManualTrading/trades?${params.toString()}`);
    return response.data.data || response.data;
  },
};

// ML Prediction API
export const mlApi = {
  getH1MfePrediction: async (): Promise<any> => {
    const response = await api.get('/ML/h1-mfe-prediction');
    return response.data;
  },

  getReversalPrediction: async (request: {
    direction: string;
    entryPrice: number;
    timeInTradeMinutes: number;
  }): Promise<any> => {
    const response = await api.post('/ML/reversal-prediction', request);
    return response.data;
  },
};

// Configuration API
export const configApi = {
  getAll: async (): Promise<ConfigCategory[]> => {
    const response = await api.get('/Configuration');
    return response.data;
  },

  getByCategory: async (category: string): Promise<ConfigCategory> => {
    const response = await api.get(`/Configuration/category/${category}`);
    return response.data;
  },

  update: async (request: UpdateConfigRequest): Promise<void> => {
    await api.post('/Configuration/update', request);
  },

  updateMany: async (requests: UpdateConfigRequest[]): Promise<void> => {
    await api.post('/Configuration/update-many', requests);
  },

  reload: async (): Promise<void> => {
    await api.post('/Configuration/reload');
  },
};

// Fund Management API
export const fundApi = {
  getSummary: async (): Promise<FundSummary> => {
    const response = await api.get('/Fund/summary');
    return response.data;
  },

  getTransactions: async (): Promise<FundTransaction[]> => {
    const response = await api.get('/Fund/transactions');
    return response.data;
  },

  deposit: async (request: DepositRequest): Promise<FundTransaction> => {
    const response = await api.post('/Fund/deposit', request);
    return response.data;
  },

  withdraw: async (request: WithdrawRequest): Promise<FundTransaction> => {
    const response = await api.post('/Fund/withdraw', request);
    return response.data;
  },
};

// Analyst Reviews API — verdicts from the claude-sdk-agent service
export const analystReviewsApi = {
  getReviews: async (limit: number = 50, instrument?: string, verdict?: string): Promise<AnalystReview[]> => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (instrument) params.append('instrument', instrument);
    if (verdict) params.append('verdict', verdict);
    const response = await api.get(`/PositionMonitor/analyst-reviews?${params.toString()}`);
    return response.data;
  },
};

// NatGasAgent discovery signals (4H trader that hunts for setups and places trades)
export const agentSignalsApi = {
  getSignals: async (limit: number = 50): Promise<AgentSignal[]> => {
    const response = await api.get(`/PositionMonitor/agent-signals?limit=${limit}`);
    return response.data;
  },
};

// Personal Advisor — runs against the claude-sdk-agent service via nginx proxy at /advisor-api/.
// Long-running requests (~2-3 min for full analysis) so we use a separate axios instance
// without the default response interceptors and with a generous timeout.
const advisorApi = axios.create({
  baseURL: '/advisor-api',
  timeout: 8 * 60 * 1000,  // 8 minutes — backend caps at 480s
});

export interface AdvisorRequestForm {
  instrument: 'NATURALGAS' | 'OIL_CRUDE' | 'US100';
  direction: 'BUY' | 'SELL';
  hasPosition: boolean;
  positionSide?: 'BUY' | 'SELL';
  entryPrice?: number;
  openedAt?: string;
  pdf?: File | null;
}

export interface AdvisorResponseData {
  analysis_markdown: string;
  instrument: string;
  direction: string;
  used_uploaded_report: boolean;
  report_date: string | null;
  tools_used: number;
  latency_ms: number;
  model: string;
}

export interface AdvisorAnalysisRow {
  id: number;
  created_at: string;
  instrument: string;
  direction: string;
  has_position: boolean;
  position_side: string | null;
  entry_price: number | null;
  opened_at: string | null;
  used_uploaded_report: boolean;
  pdf_report_date: string | null;
  analysis_markdown: string | null;
  status: 'ok' | 'timeout' | 'error';
  error_detail: string | null;
  tools_used: number | null;
  latency_ms: number | null;
  model: string | null;
}

export const personalAdvisorApi = {
  analyze: async (req: AdvisorRequestForm): Promise<AdvisorResponseData> => {
    const fd = new FormData();
    fd.append('instrument', req.instrument);
    fd.append('direction', req.direction);
    fd.append('has_position', String(req.hasPosition));
    if (req.hasPosition) {
      if (req.positionSide) fd.append('position_side', req.positionSide);
      if (req.entryPrice != null) fd.append('entry_price', String(req.entryPrice));
      if (req.openedAt) fd.append('opened_at', req.openedAt);
    }
    if (req.pdf) fd.append('pdf', req.pdf);
    const response = await advisorApi.post('/personal-advisor/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadNatGasReport: async (file: File): Promise<{ ok: boolean; report_date: string; raw_text_len: number; extracted_field_count: number }> => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await advisorApi.post('/personal-advisor/upload-natgas-report', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getAnalyses: async (limit: number = 100, instrument?: string, direction?: string): Promise<AdvisorAnalysisRow[]> => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (instrument) params.append('instrument', instrument);
    if (direction) params.append('direction', direction);
    const response = await advisorApi.get(`/personal-advisor/analyses?${params.toString()}`, {
      timeout: 30 * 1000,
    });
    return response.data;
  },
};

// Scout (experimental NATURALGAS pipeline) — on-demand discovery run.
// Same backend service as advisor (claude-sdk-agent), reachable via /advisor-api proxy.
export interface ScoutVerdict {
  action: 'TRADE' | 'PASS';
  direction: 'BUY' | 'SELL' | null;
  entry_price: number | null;
  setup_type: string | null;
  confidence: number;
  reasoning: string;
  plain_english_summary: string;
  metadata?: {
    ran_at_utc?: string;
    latency_ms?: number;
    tools_used?: number;
    model?: string;
    error?: string;
    skipped?: boolean;
    reason?: string;
    raw_text_preview?: string;
  };
}

export interface StructureBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}
export interface StructureSwing {
  time: number;
  price: number;
  label: 'H1' | 'HH' | 'LH' | 'L1' | 'HL' | 'LL';
  kind: 'high' | 'low';
  bar_index_from_latest: number;
}
export interface StructureZone {
  zone_low: number;
  zone_mid: number;
  zone_high: number;
  zone_width_atr: number | null;
  zone_type: 'DEMAND' | 'SUPPLY' | 'FLIP';
  touches: number;
  newest_touch_age_bars: number;
  oldest_touch_age_bars: number;
  types: string[];
}
export interface NextRetestZone {
  type: string;
  anchor_price: number;
  zone_low: number;
  zone_high: number;
  interpretation: string;
}
export interface StructurePayload {
  instrument: string;
  resolution: string;
  current_price: number;
  atr_14: number | null;
  trend_state: string;
  bars: StructureBar[];
  highs: StructureSwing[];
  lows: StructureSwing[];
  zones: StructureZone[];
  next_retest_zone: NextRetestZone | null;
}

export interface ScoutScheduledRun {
  run_id: string;
  ran_at: string;
  instrument: string;
  action: 'WATCH' | 'TRADE' | 'PASS' | 'SKIPPED' | 'AGENT_ERROR' | 'EXIT_REVIEW';
  direction: 'BUY' | 'SELL' | null;
  setup_type: string | null;
  entry_price: number | null;
  confidence: number | null;
  reasoning: string | null;
  plain_english_summary: string | null;
  skipped_at_step: string | null;
  skip_reason: string | null;
  trade_id: string | null;
  placement_status: string | null;
  latency_ms: number | null;
  tools_used: number | null;
  model: string | null;
}

export interface ScoutTriggerCondition {
  type: string;
  level?: number;
  low?: number;
  high?: number;
  pct?: number;
  ratio?: number;
  lookback_bars?: number;
  window_bars?: number;
  tf?: string;
}

export interface ScoutTriggerEvalResult {
  type: string;
  ok: boolean;
  debug: string;
}

export interface ScoutWatchTrigger {
  trigger_id: string;
  parent_run_id: string;
  created_at: string;
  expires_at: string;
  instrument: string;
  name: string;
  side: 'BUY' | 'SELL';
  setup_type: string | null;
  confidence: number | null;
  conditions: ScoutTriggerCondition[];
  trade_size: number | null;
  trade_stop_price: number | null;
  trade_take_profit: number | null;
  reasoning: string | null;
  status: 'ACTIVE' | 'MATCHED' | 'EXPIRED' | 'CANCELLED' | 'REPLACED';
  matched_at: string | null;
  matched_trade_id: string | null;
  cancelled_reason: string | null;
  last_eval_at: string | null;
  last_eval_result: ScoutTriggerEvalResult[] | null;
  trigger_kind: 'ENTRY' | 'EXIT';
  trade_id: string | null;
  close_reason: string | null;
}

export const scoutApi = {
  runNow: async (): Promise<ScoutVerdict> => {
    const response = await advisorApi.post('/experimental-ng/run');
    return response.data;
  },
  getStructure: async (
    instrument: string = 'NATURALGAS',
    resolution: string = 'HOUR_4',
    lookbackBars: number = 120,
  ): Promise<StructurePayload> => {
    const response = await advisorApi.get('/scout/structure', {
      params: { instrument, resolution, lookback_bars: lookbackBars },
      timeout: 30 * 1000,
    });
    return response.data;
  },
  getScheduledRuns: async (limit: number = 200): Promise<ScoutScheduledRun[]> => {
    const response = await advisorApi.get('/scout/scheduled-runs', {
      params: { limit },
      timeout: 30 * 1000,
    });
    return response.data;
  },
  getWatchTriggers: async (
    status: 'all' | 'active' | 'matched' | 'expired' | 'cancelled' | 'replaced' = 'all',
    kind: 'all' | 'entry' | 'exit' = 'all',
    limit: number = 100,
  ): Promise<ScoutWatchTrigger[]> => {
    const response = await advisorApi.get('/scout/watch-triggers', {
      params: { status, kind, limit },
      timeout: 30 * 1000,
    });
    return response.data;
  },
  /** Latest entry-mode strategist fire per instrument (≤3 rows). */
  getLatestEntryReviews: async (): Promise<ScoutScheduledRun[]> => {
    const response = await advisorApi.get('/scout/latest-entry-reviews', {
      timeout: 30 * 1000,
    });
    return response.data;
  },
  /** Latest ACTIVE trigger per (instrument, kind) — at most 6 rows. */
  getActiveTriggersLatest: async (): Promise<ScoutWatchTrigger[]> => {
    const response = await advisorApi.get('/scout/active-triggers-latest', {
      timeout: 30 * 1000,
    });
    return response.data;
  },
  getExitReviews: async (
    limit: number = 100,
    latestPerTrade: boolean = false,
    instrument: ScoutInstrument | 'ALL' = 'ALL',
    scope: 'today' | 'week' | 'all' = 'today',
  ): Promise<ScoutExitReview[]> => {
    const response = await advisorApi.get('/scout/exit-reviews', {
      params: { limit, latest_per_trade: latestPerTrade, instrument, scope },
      timeout: 30 * 1000,
    });
    return response.data;
  },
  getTradeStats: async (
    scope: 'today' | 'week' | 'all' = 'today',
    instrument: ScoutInstrument | 'ALL' = 'ALL',
  ): Promise<ScoutTradeStats> => {
    const response = await advisorApi.get('/scout/trade-stats', {
      params: { scope, instrument },
      timeout: 30 * 1000,
    });
    return response.data;
  },
  /** Current directional bias for all 3 instruments (always returns all 3 keys). */
  getBias: async (): Promise<Record<ScoutInstrument, BiasState>> => {
    const response = await advisorApi.get('/scout/bias', { timeout: 15 * 1000 });
    return response.data;
  },
  /** Set the bias for an instrument. direction='NONE' pauses entries.
   * When direction differs from prior, backend auto-cancels stale ACTIVE entry triggers. */
  setBias: async (req: {
    instrument: ScoutInstrument;
    direction: 'BUY' | 'SELL' | 'NONE';
    set_by?: string;
    notes?: string;
    validity_hours?: number;
  }): Promise<{ bias: BiasState; prior_direction: string; cancelled_stale_triggers: number }> => {
    const response = await advisorApi.post('/scout/bias', req, { timeout: 15 * 1000 });
    return response.data;
  },
  /** All currently OPEN experimental trades with broker-side context. */
  getOpenTrades: async (): Promise<ScoutOpenTrade[]> => {
    const response = await advisorApi.get('/scout/open-trades', { timeout: 20 * 1000 });
    return response.data;
  },
  /** Manual close: hits broker, marks DB closed, returns realized P/L if available. */
  closeTrade: async (trade_id: string, reason?: string): Promise<{
    trade_id: string;
    broker_status: string;
    realized_pnl_mxn: number | null;
    tx_ref: string | null;
    reason: string;
  }> => {
    const response = await advisorApi.post(
      `/scout/trades/${encodeURIComponent(trade_id)}/close`,
      null,
      { params: reason ? { reason } : {}, timeout: 30 * 1000 },
    );
    return response.data;
  },
};

export interface BiasState {
  direction: 'BUY' | 'SELL' | 'NONE';
  set_at: string | null;
  expires_at: string | null;
  hours_ago: number | null;
  set_by: string | null;
  notes: string | null;
}

export interface ScoutOpenTrade {
  trade_id: string;
  instrument: ScoutInstrument | string;
  direction: 'BUY' | 'SELL';
  size: number | null;
  entry_price: number;
  stop_price: number | null;
  take_profit: number | null;
  take_profit_2: number | null;
  entry_setup_type: string | null;
  entry_confidence: number | null;
  created_at: string | null;
  hours_held: number | null;
  current_price: number | null;
  unrealized_pl: number | null;
  broker_status: string;
  deal_id: string | null;
}

/** The 3 instruments traded by the experimental scout pipeline. Named with the
 * Scout prefix to avoid collision with the existing `Instrument` interface in
 * src/types (which models a full Capital.com epic record, not a string union). */
export type ScoutInstrument = 'NATURALGAS' | 'OIL_CRUDE' | 'US500';
export const INSTRUMENTS: ScoutInstrument[] = ['NATURALGAS', 'OIL_CRUDE', 'US500'];
export const INSTRUMENT_DISPLAY: Record<ScoutInstrument, string> = {
  NATURALGAS: 'NG',
  OIL_CRUDE:  'OIL',
  US500:      'US500',
};

export interface ScoutTradeStatsAggregate {
  total_trades: number;
  open_count: number;
  closed_count: number;
  rejected_count: number;
  wins: number;
  losses: number;
  win_rate_pct: number | null;
  realized_pl: number;
  avg_pl: number | null;
  best_pl: number | null;
  worst_pl: number | null;
}

export interface ScoutTradeStats extends ScoutTradeStatsAggregate {
  scope: 'today' | 'week' | 'all' | string;
  instrument: ScoutInstrument | 'ALL';
  /** Per-instrument breakdown — always returned, ignores the instrument filter. */
  per_instrument: Partial<Record<ScoutInstrument, ScoutTradeStatsAggregate>>;
}

export interface ScoutExitReview {
  review_id: string;
  trade_id: string;
  /** Joined from experimental_trades. May be null if the trade row was deleted. */
  instrument: ScoutInstrument | null;
  direction: 'BUY' | 'SELL' | null;
  reviewed_at: string;
  current_price: number | null;
  unrealized_pl: number | null;
  bars_held: number | null;
  decision: 'HOLD' | 'CLOSE_NOW' | 'ARMED' | 'CLOSE' | string;
  decision_confidence: number | null;
  reasoning: string | null;
  plain_english_summary: string | null;
  latency_ms: number | null;
  tools_used: number | null;
  fail_open: boolean;
}

export default api;
